import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { actors } from "@/mocks/actors";
import { actorRegions } from "@/utils/catalog";
import ActorCard from "@/components/base/ActorCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FilterGroup from "@/pages/category/components/FilterGroup";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 12;

const genderOptions = [
  { value: "男", label: "男演员" },
  { value: "女", label: "女演员" },
];

const heatOptions = [
  { value: "hot", label: "热度高" },
  { value: "rising", label: "新锐推荐" },
];

export default function ActorCategory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [regions, setRegions] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [heats, setHeats] = useState<string[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useSeo(
    "演员资料 | 葵司影视资讯",
    "葵司演员资料频道收录热门演员的详细资料与代表作品，支持按地区、性别、热门程度筛选，快速找到你关注的演员。",
    "葵司,演员资料,演员列表,演员简介,影视演员",
  );

  const currentPage = Math.max(
    1,
    Number.parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );

  const toggle = (
    list: string[],
    setList: (v: string[]) => void,
    value: string,
  ) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      return next;
    });
  };

  const resetAll = () => {
    setRegions([]);
    setGenders([]);
    setHeats([]);
    setSearchParams({});
  };

  const filtered = useMemo(() => {
    let list = [...actors];
    if (regions.length) list = list.filter((a) => regions.includes(a.region));
    if (genders.length) list = list.filter((a) => genders.includes(a.gender));
    if (heats.length) {
      list = list.filter((a) =>
        heats.some((h) =>
          h === "hot" ? a.popularity >= 88 : a.popularity < 88,
        ),
      );
    }
    return list.sort((a, b) => b.popularity - a.popularity);
  }, [regions, genders, heats]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageItems = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete("page");
      else next.set("page", String(page));
      return next;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const regionOptions = actorRegions.map((r) => ({ value: r, label: r }));

  const filterContent = (
    <div className="flex flex-col gap-5">
      <FilterGroup
        label="地区"
        options={regionOptions}
        selected={regions}
        onToggle={(v) => toggle(regions, setRegions, v)}
      />
      <FilterGroup
        label="性别"
        options={genderOptions}
        selected={genders}
        onToggle={(v) => toggle(genders, setGenders, v)}
      />
      <FilterGroup
        label="热门程度"
        options={heatOptions}
        selected={heats}
        onToggle={(v) => toggle(heats, setHeats, v)}
      />
    </div>
  );

  const activeCount = regions.length + genders.length + heats.length;

  return (
    <div>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "演员资料" }]} />
          <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950">
            演员资料
          </h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-foreground-600">
            汇集电影、电视剧、综艺领域的演员资料，涵盖基本信息、代表作品与演艺经历，帮助你全面了解喜欢的演员。
          </p>
        </div>
      </div>

      <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="hidden lg:block rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between gap-4 mb-5">
            <span className="font-heading font-bold text-foreground-950">
              筛选条件
            </span>
            <button
              type="button"
              onClick={resetAll}
              className="text-sm text-foreground-500 hover:text-accent-600 cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
          {filterContent}
        </div>

        <div className="lg:hidden flex items-center justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="h-10 px-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line" />
            筛选
            {activeCount > 0 && (
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-500 text-background-950 text-xs">
                {activeCount}
              </span>
            )}
          </button>
          <p className="text-sm text-foreground-500">
            共 <span className="text-accent-600 font-medium">{filtered.length}</span> 位演员
          </p>
        </div>

        <p className="hidden lg:block mt-5 mb-6 text-sm text-foreground-500">
          共找到 <span className="text-accent-600 font-medium">{filtered.length}</span> 位演员
        </p>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {pageItems.map((a) => (
                <ActorCard key={a.id} actor={a} />
              ))}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50">
            <EmptyState
              icon="ri-filter-off-line"
              title="没有符合条件的演员"
              description="试试调整或重置筛选条件。"
              actionLabel="重置筛选"
              onAction={resetAll}
            />
          </div>
        )}
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-950/40"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-bold text-lg text-foreground-950">
                筛选条件
              </span>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {filterContent}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 h-11 rounded-md border border-background-200 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer hover:bg-primary-600 transition-colors"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-content mx-auto px-4 md:px-6 pb-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-accent-600"
        >
          <i className="ri-arrow-left-line" />
          返回首页
        </Link>
      </div>
    </div>
  );
}